import React, { useEffect, useState } from 'react';
import Input from '../../ui/input';
import Label from '../../ui/label';
import Select from '../../ui/select';
import { getSites } from '../../../redux/api/siteAPI';
import { validateUserEdit, validateUserEditNoPassW } from '../../../model/UserModel';
import { userUpdate } from '../../../redux/api/userAPI';
import Button from '../../ui/button';
import { Icon } from '../../../assets/icons';
import { useTranslation } from 'react-i18next';

const sites = [
  { Site_ID: 1, Name: 'yard 1' },
  { Site_ID: 2, Name: 'Room 2' },
  { Site_ID: 3, Name: 'Hallway 1' },
];

const notificationChannels = [
  { name: 'Email_Notifications', labelKey: 'email_notifications' },
  { name: 'Slack_Notifications', labelKey: 'slack_notifications' },
  { name: 'SMS_Notifications', labelKey: 'sms_notifications' },
  { name: 'Telegram_Notifications', label: 'Telegram Notifications' },
];

const EditUserModal = ({ data, isOpen, onClose }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    Company_ID: null,
    user_ID: null,
    First_name: '',
    Last_name: '',
    Position: '',
    Phone: '',
    Email: '',
    Role: '',
    Language: 'English',
    Password: '',
    CPassword: '',
    Notifications: 0,
    Email_Notifications: 0,
    SMS_Notifications: 0,
    Slack_Notifications: 0,
    Telegram_Notifications: 0,
    Telegram_chat_id: '',
    Site_ID: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [errorEdit, setErrorEdit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sitesList, setSitesList] = useState([]);
  const [notificationChannelsOpen, setNotificationChannelsOpen] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const sitesRes = await getSites();
        const fetchedSites = sitesRes.data?.sites || sites;
        setSitesList(fetchedSites);

        // Initialize formData after sites are fetched to ensure Site_ID mapping
        setFormData({
          Company_ID: data?.Company_ID || null,
          user_ID: data?.user_ID || null,
          First_name: data?.First_name || '',
          Last_name: data?.Last_name || '',
          Position: data?.Position || '',
          Phone: data?.Phone || '',
          Email: data?.Email || '',
          Role: data?.Role || '',
          Language: data?.Language || 'English',
          Password: '',
          CPassword: '',
          Notifications: data?.Notifications ? 1 : 0,
          Email_Notifications: data?.Email_Notifications ? 1 : 0,
          SMS_Notifications: data?.SMS_Notifications ? 1 : 0,
          Slack_Notifications: data?.Slack_Notifications ? 1 : 0,
          Telegram_Notifications: data?.Telegram_Notifications ? 1 : 0,
          Telegram_chat_id: data?.Telegram_chat_id || '',
          Site_ID: data?.site?.Site_ID || null,
        });
      } catch (err) {
        console.error('Error fetching sites:', err);
        setSitesList(sites);
        // Initialize formData even if sites fetch fails
        setFormData({
          Company_ID: data?.Company_ID || null,
          user_ID: data?.user_ID || null,
          First_name: data?.First_name || '',
          Last_name: data?.Last_name || '',
          Position: data?.Position || '',
          Phone: data?.Phone || '',
          Email: data?.Email || '',
          Role: data?.Role || '',
          Language: data?.Language || 'English',
          Password: '',
          CPassword: '',
          Notifications: data?.Notifications ? 1 : 0,
          Email_Notifications: data?.Email_Notifications ? 1 : 0,
          SMS_Notifications: data?.SMS_Notifications ? 1 : 0,
          Slack_Notifications: data?.Slack_Notifications ? 1 : 0,
          Telegram_Notifications: data?.Telegram_Notifications ? 1 : 0,
          Telegram_chat_id: data?.Telegram_chat_id || '',
          Site_ID: data?.site?.Site_ID || null,
        });
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && data) {
      fetchInitialData();
    }
  }, [isOpen, data]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let transformedValue = value;

    if (type === 'checkbox') {
      transformedValue = checked ? 1 : 0;
      if (name === 'Notifications' && !checked) {
        setFormData((prevState) => ({
          ...prevState,
          Notifications: 0,
          Email_Notifications: 0,
          SMS_Notifications: 0,
          Slack_Notifications: 0,
          Telegram_Notifications: 0,
        }));
        return;
      }
    } else if (name === 'First_name' || name === 'Last_name' || name === 'Position') {
      transformedValue = value.charAt(0).toUpperCase() + value.slice(1);
    }

    setFormData((prevState) => ({
      ...prevState,
      [name]: transformedValue,
    }));

    if (formErrors[name]) {
      setFormErrors((prevState) => ({
        ...prevState,
        [name]: '',
      }));
    }
  };

  const validateForm = async () => {
    try {
      if (formData.Password) {
        await validateUserEdit(formData);
      } else {
        await validateUserEditNoPassW(formData);
      }
      return true;
    } catch (error) {
      const validationErrors = {};
      error.inner?.forEach((err) => {
        validationErrors[err.path] = err.message;
      });
      setFormErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (isValid) {
      try {
        const result = await userUpdate(formData);
        if (result.data) {
          closeModal(true);
        } else {
          setErrorEdit(result.error?.response?.data?.detail || 'Error updating user');
        }
      } catch (error) {
        console.error('Error updating user:', error);
        setErrorEdit(error.response?.data?.detail || 'Error updating user');
      }
    }
  };

  const closeModal = (refresh = false) => {
    setFormData({
      Company_ID: null,
      user_ID: null,
      First_name: '',
      Last_name: '',
      Position: '',
      Phone: '',
      Email: '',
      Role: '',
      Language: 'English',
      Password: '',
      CPassword: '',
      Notifications: 0,
      Email_Notifications: 0,
      SMS_Notifications: 0,
      Slack_Notifications: 0,
      Telegram_Notifications: 0,
      Telegram_chat_id: '',
      Site_ID: null,
    });
    setFormErrors({});
    setErrorEdit(null);
    setNotificationChannelsOpen(false);
    onClose(refresh);
  };

  const generateRandomPassword = () => {
    const length = 8;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';

    const specialChar = '!@#$%^&*';
    password += specialChar[Math.floor(Math.random() * specialChar.length)];

    const randomUppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    password += randomUppercase[Math.floor(Math.random() * randomUppercase.length)];

    const randomLowercase = 'abcdefghijklmnopqrstuvwxyz';
    password += randomLowercase[Math.floor(Math.random() * randomLowercase.length)];

    const randomDigit = '0123456789';
    password += randomDigit[Math.floor(Math.random() * randomDigit.length)];

    for (let i = 0; i < length - 4; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      password += charset[randomIndex];
    }

    password = password.split('').sort(() => Math.random() - 0.5).join('');

    setFormData((prevState) => ({
      ...prevState,
      Password: password,
      CPassword: password,
    }));
    setFormErrors((prevState) => ({ ...prevState, Password: '', CPassword: '' }));
  };

  return (
    <form data-testid="user-edit-form" onSubmit={handleSubmit}>
      <div className="min-w-[350px] overflow-y-auto grid gap-6 max-md:grid-cols-1 grid-cols-2 w-auto m-2 bg-white max-md:p-3 p-8 rounded-3xl shadow-lg">
        <p className="flex justify-center col-span-full text-xl text-[#4C4C66] font-bold mb-4">
          {t('edit_user_details')}
        </p>
        <Input
          required
          label={t('first_name')}
          name="First_name"
          value={formData.First_name}
          onChange={handleChange}
          placeholder={t('enter_first_name')}
          error={formErrors.First_name}
        />
        <Input
          required
          label={t('last_name')}
          name="Last_name"
          value={formData.Last_name}
          onChange={handleChange}
          placeholder={t('enter_last_name')}
          error={formErrors.Last_name}
        />
        <Input
          required
          label={t('email')}
          name="Email"
          value={formData.Email}
          onChange={handleChange}
          placeholder={t('enter_email_address')}
          error={formErrors.Email}
        />
        <Input
          required
          label={t('phone')}
          name="Phone"
          phone
          value={formData.Phone}
          onChange={handleChange}
          placeholder={t('enter_phone_number')}
          error={formErrors.Phone}
        />
        <div className="col-span-full">
          <Input
            required
            label={t('position')}
            name="Position"
            value={formData.Position}
            onChange={handleChange}
            placeholder={t('enter_position')}
            error={formErrors.Position}
          />
        </div>
        <Select
          required
          selectOnly
          options={[
            { label: t('admin'), value: 'admin' },
            { label: t('analyst'), value: 'analyst' },
            // { label: t('user'), value: "user" },
          ]}
          label={t('role')}
          isSearchable={false}
          name="Role"
          value={formData.Role ? { label: t(formData.Role), value: formData.Role } : null}
          onChange={(option) => setFormData((prevState) => ({ ...prevState, Role: option.value }))}
          placeholder={t('select_role')}
          error={formErrors.Role}
        />
        <Select
          required
          options={sitesList.map((site) => ({ label: site.Name, value: site.Site_ID }))}
          label={t('site')}
          name="Site_ID"
          value={
            formData.Site_ID && sitesList.length
              ? {
                  label: sitesList.find((site) => site.Site_ID === formData.Site_ID)?.Name || data?.site?.Name || '',
                  value: formData.Site_ID,
                }
              : null
          }
          onChange={(option) => setFormData((prevState) => ({ ...prevState, Site_ID: option.value }))}
          placeholder={t('select_site')}
          error={formErrors.Site_ID}
        />
        <div className="col-span-full">
          <p className="text-lg text-[#4C4C66] font-semibold mb-4">{t('notification_settings')}</p>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div
              className={`flex ${
                formData.Language === 'ar' ? 'flex-row-reverse' : 'flex-row'
              } items-center justify-between py-2`}
            >
              <div className="text-gray-800 font-medium">
                <Label setValue={t('notifications')} />
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  name="Notifications"
                  type="checkbox"
                  checked={!!formData.Notifications}
                  onChange={handleChange}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#0071BC]"></div>
              </label>
            </div>
            {formData.Notifications ? (
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setNotificationChannelsOpen((open) => !open)}
                  className="flex items-center gap-1 text-xs font-semibold text-[#0071BC]"
                  aria-expanded={notificationChannelsOpen}
                >
                  {t('view_notification_channels')}
                  <Icon
                    icon="ChevronDown"
                    className={`w-3 h-3 transition-transform ${notificationChannelsOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {notificationChannelsOpen && (
                  <div className="mt-3 space-y-2 pl-4 border-l-2 border-gray-300">
                    {notificationChannels.map((channel) => (
                      <div
                        key={channel.name}
                        className={`flex ${
                          formData.Language === 'ar' ? 'flex-row-reverse' : 'flex-row'
                        } items-center justify-between py-2`}
                      >
                        <div className="text-gray-700">
                          <Label setValue={channel.label || t(channel.labelKey)} />
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            name={channel.name}
                            type="checkbox"
                            checked={!!formData[channel.name]}
                            onChange={handleChange}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#0071BC]"></div>
                        </label>
                      </div>
                    ))}
                    <Input
                      label="Telegram chat ID"
                      name="Telegram_chat_id"
                      value={formData.Telegram_chat_id || ''}
                      onChange={handleChange}
                      placeholder="Optional; company default is used when blank"
                    />
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className="col-span-full">
          <div className="flex items-center justify-between mb-4">
            <p className="text-lg text-[#4C4C66] font-semibold">{t('password')}</p>
            <button
              type="button"
              onClick={generateRandomPassword}
              title={t('generate_secure_password')}
              aria-label={t('generate_secure_password')}
              className="flex items-center justify-center w-8 h-8 rounded-md text-gray-400 hover:text-[#0071BC] hover:bg-gray-100"
            >
              <Icon icon="Reload" className="w-4 h-4" />
            </button>
          </div>
          <div className="grid gap-4 max-md:grid-cols-1 grid-cols-2">
            <Input
              type="password"
              label={t('password')}
              name="Password"
              value={formData.Password}
              onChange={handleChange}
              placeholder={t('enter_password')}
              error={formErrors.Password}
            />
            <Input
              type="password"
              label={t('confirm_new_password')}
              name="CPassword"
              value={formData.CPassword}
              onChange={handleChange}
              placeholder={t('confirm_new_password')}
              error={formErrors.CPassword}
            />
          </div>
          <p className="text-xs text-gray-400 mt-1">{t('keep_current_password_hint')}</p>
        </div>
        {errorEdit && (
          <div className="col-span-full flex justify-center">
            <span className="my-4 text-xs text-[#FF0000]">
              {t('error_updating_user', { message: errorEdit })}
            </span>
          </div>
        )}
        <div className="col-span-full flex justify-center mt-10 space-x-5">
          <Button type="reset" onClick={() => closeModal(false)} size="large" color="cancel">
            {t('cancel')}
          </Button>
          <Button data-testid="user-edit-submit" type="submit" size="large" disabled={loading}>
            {t('update_user')}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default EditUserModal;
